import { useEffect, useRef } from 'react'
import { useAppDispatch } from './hooks'
import { logoutAsync, showSessionExpiryWarning, selectUserIsAuthenticated } from 'store/authslice'
import { useSelector } from 'react-redux'

const WARNING_BEFORE_EXPIRY_MS = 3 * 60 * 1000 // 3 minutes before expiry
const SESSION_MAX_MS = 30 * 60 * 1000 // 30 minutes max session

const getTokenExpiry = (): number | null => {
  try {
    const raw = localStorage.getItem('token')
    if (!raw) return null
    const token = JSON.parse(raw) as string
    const payload = JSON.parse(atob(token.split('.')[1]))
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

export const useSessionExpiry = () => {
  const dispatch = useAppDispatch()
  const isAuthenticated = useSelector(selectUserIsAuthenticated)
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimers = () => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
    if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current)
  }

  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers()
      return
    }

    const scheduleTimers = () => {
      clearTimers()

      const expiryMs = getTokenExpiry()
      if (!expiryMs) return

      const now = Date.now()
      const msUntilExpiry = Math.min(expiryMs - now, SESSION_MAX_MS)
      const msUntilWarning = msUntilExpiry - WARNING_BEFORE_EXPIRY_MS

      if (msUntilExpiry <= 0) {
        dispatch(logoutAsync())
        return
      }

      if (msUntilWarning > 0) {
        warningTimerRef.current = setTimeout(() => {
          dispatch(showSessionExpiryWarning())
        }, msUntilWarning)
      } else {
        // Less than 3 minutes remaining — show warning immediately
        dispatch(showSessionExpiryWarning())
      }

      expiryTimerRef.current = setTimeout(() => {
        dispatch(logoutAsync())
      }, msUntilExpiry)
    }

    scheduleTimers()

    return () => clearTimers()
  }, [isAuthenticated, dispatch])
}
